/**
 * นำเข้าข้อมูลย้อนหลัง (OWNER) — เงินเดือน/ภาษี/ปกส. รายเดือน + ประวัติการลา
 * ให้ข้อมูลครบทั้งปี (สำหรับภาษีสะสม + รายงานปลายปี ภงด.1/50ทวิ/ปกส.).
 */
import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useAuthStore } from '../../lib/store';
import { useToast } from '../../components/ui/toast';
import { Upload, Download, FileSpreadsheet, AlertTriangle, CheckCircle2, Users } from 'lucide-react';
import { downloadRegistryXlsx } from '../../lib/registry-xlsx';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://localhost:8787';

interface ImportResult {
  /** เส้นทางเดิมรายงานเป็น written · เส้นทางใหม่แยกเป็น inserted/updated */
  written?: number;
  inserted?: number;
  updated?: number;
  total?: number;
  skipped: number;
  /** fb#158 — ร่างค้างของเดือนเก่าที่ถูกแทนด้วยยอดนำเข้า */
  replaced_drafts?: number;
  errors: Array<{ row: number; msg: string; emp_code?: string }>;
  years_recomputed?: number[];
  /** fb#254 — นำเข้าไฟล์รายงานระบบเดิม: งวดที่อ่านจากหัวรายงาน */
  period_from_report?: string;
  report_range?: string;
}

/** สรุปผลเป็นข้อความเดียวไม่ว่าเส้นทางไหนตอบมา */
function resultSummary(r: ImportResult): string {
  const parts: string[] = [];
  if (typeof r.inserted === 'number') parts.push(`เพิ่มใหม่ ${r.inserted}`);
  if (typeof r.updated === 'number' && r.updated > 0) parts.push(`อัปเดต ${r.updated}`);
  if (parts.length === 0 && typeof r.written === 'number') parts.push(`นำเข้า ${r.written}`);
  if (r.skipped > 0) parts.push(`ข้าม ${r.skipped}`);
  if ((r.replaced_drafts ?? 0) > 0) parts.push(`แทนร่างค้าง ${r.replaced_drafts}`);
  return parts.join(' · ') || 'ไม่มีรายการที่นำเข้า';
}

function downloadCsv(filename: string, header: string[], example: string[]) {
  const csv = '﻿' + header.join(',') + '\n' + example.join(',') + '\n';
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function ImportCard({
  title, description, endpoint, templateName, columns, example, note, currentList,
}: {
  title: string;
  description: string;
  endpoint: string;
  templateName: string;
  columns: { key: string; label: string; required?: boolean }[];
  example: string[];
  note?: string;
  /** ปุ่มโหลด "รายชื่อปัจจุบัน" จากระบบ (ไม่ใช่เทมเพลตเปล่า) ให้แก้แล้วอัปกลับ — ทางลัดแก้ทั้งบริษัท */
  currentList?: { endpoint: string; filename: string; label: string; hint: string };
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  async function downloadCurrent() {
    if (!currentList) return;
    setDownloading(true);
    try {
      await downloadRegistryXlsx(currentList.endpoint, currentList.filename);
      toast.success('ดาวน์โหลดรายชื่อปัจจุบันแล้ว — แก้ในชีตแล้วอัปโหลดกลับได้เลย');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'ดาวน์โหลดไม่สำเร็จ');
    } finally {
      setDownloading(false);
    }
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const jwt = useAuthStore.getState().jwt;
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
        body: form,
      });
      const json = (await res.json()) as { data?: ImportResult; error?: string; message?: string };
      if (!res.ok) throw new Error(json.error || json.message || `HTTP ${res.status}`);
      setResult(json.data ?? null);
      if (json.data) toast.success(resultSummary(json.data));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'นำเข้าไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card p-5 space-y-3">
      <div className="flex items-start gap-2">
        <FileSpreadsheet className="w-5 h-5 text-teal-600 mt-0.5" />
        <div>
          <h3 className="font-semibold">{title}</h3>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr><th className="px-2 py-1 text-left">คอลัมน์</th><th className="px-2 py-1 text-left">ความหมาย</th></tr>
          </thead>
          <tbody>
            {columns.map((c) => (
              <tr key={c.key} className="border-t border-gray-100">
                <td className="px-2 py-1 font-mono">{c.key}{c.required && <span className="text-red-500">*</span>}</td>
                <td className="px-2 py-1 text-gray-600">{c.label}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {note && (
        <p className="text-xs text-amber-700 bg-amber-50 rounded p-2 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{note}
        </p>
      )}

      {currentList && (
        <p className="text-xs text-teal-800 bg-teal-50 rounded p-2 flex items-start gap-1.5">
          <Users className="w-3.5 h-3.5 shrink-0 mt-0.5" />{currentList.hint}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {currentList && (
          <button onClick={downloadCurrent} disabled={downloading} className="btn-secondary">
            <Users className="w-4 h-4" /> {downloading ? 'กำลังโหลด…' : currentList.label}
          </button>
        )}
        <button onClick={() => downloadCsv(templateName, columns.map((c) => c.key), example)} className="btn-secondary">
          <Download className="w-4 h-4" /> ดาวน์โหลดเทมเพลต
        </button>
        <label className="btn-primary cursor-pointer">
          <Upload className="w-4 h-4" /> {busy ? 'กำลังนำเข้า…' : 'อัปโหลด CSV'}
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} disabled={busy} />
        </label>
      </div>

      {result && (
        <div className="rounded-lg bg-gray-50 p-3 text-sm space-y-1">
          <div className="flex items-center gap-2 text-emerald-700">
            <CheckCircle2 className="w-4 h-4" /> {resultSummary(result)}
          </div>
          {result.period_from_report && (
            <div className="text-xs text-gray-600">อ่านช่วงเวลาจากหัวรายงาน "{result.report_range}" → ลงเป็นยอดสะสมแถวเดียวที่งวด {result.period_from_report}</div>
          )}
          {result.years_recomputed && result.years_recomputed.length > 0 && (
            <div className="text-xs text-gray-500">อัปเดตยอดสะสมปี: {result.years_recomputed.join(', ')}</div>
          )}
          {result.errors.length > 0 && (
            <div className="text-xs text-red-600">
              <div className="font-medium">ผิดพลาด {result.errors.length} แถว:</div>
              <ul className="list-disc ml-4 max-h-32 overflow-y-auto">
                {result.errors.slice(0, 50).map((er, i) => (
                  <li key={i}>แถว {er.row}{er.emp_code ? ` (${er.emp_code})` : ''}: {er.msg}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StepHeader({ n, title, detail }: { n: number; title: string; detail: string }) {
  return (
    <div className="flex items-start gap-3 pt-2">
      <div className="w-7 h-7 shrink-0 rounded-full bg-teal-600 text-white text-sm font-bold flex items-center justify-center">
        {n}
      </div>
      <div>
        <h2 className="font-bold text-gray-900">{title}</h2>
        <p className="text-sm text-gray-500">{detail}</p>
      </div>
    </div>
  );
}

export function HistoricalImport() {
  return (
    <AdminLayout title="นำเข้าข้อมูลจากไฟล์" subtitle="ตั้งต้นระบบจากไฟล์ Excel — ดาวน์โหลดเทมเพลต กรอก แล้วอัปโหลดกลับ">
      <div className="space-y-5">
        <p className="card p-3 text-sm text-gray-600 bg-blue-50 border-blue-100">
          ทุกชุดใช้วิธีเดียวกัน: กด <b>ดาวน์โหลดเทมเพลต</b> → เปิดด้วย Excel หรือ Google Sheets →
          กรอกทับบรรทัดตัวอย่าง → บันทึกเป็น CSV → กด <b>อัปโหลด</b>
          <br />
          <b>ลำดับสำคัญ</b> — ต้องนำเข้าพนักงานก่อนเสมอ เพราะชุดอื่นอ้างอิงรหัสพนักงาน
          ถ้าอัปโหลดสลับลำดับ ระบบจะบอกว่าไม่พบรหัสนั้นแล้วข้ามแถวให้
        </p>

        <StepHeader n={1} title="ทะเบียนพนักงาน" detail="ต้องมีก่อนชุดอื่นทั้งหมด" />

        <ImportCard
          title="รายชื่อพนักงาน"
          description="ข้อมูลทั่วไปของพนักงาน — รหัสซ้ำ = อัปเดตคนเดิม (ไม่ใช่เพิ่มคนใหม่) · แถวของคนเดิมใส่แค่คอลัมน์ที่จะแก้ก็พอ"
          endpoint="/owner/employees/import"
          templateName="employees-template.csv"
          currentList={{
            endpoint: '/owner/employees/org-export',
            filename: 'employees-registry.xlsx',
            label: 'ดาวน์โหลดทะเบียนพนักงานปัจจุบัน (ครบทุกคอลัมน์ + เลขบัญชี)',
            hint: 'ไฟล์ทะเบียนใช้คอลัมน์เดียวกับเทมเพลต + ธนาคาร/เลขบัญชี และคอลัมน์ "ตรวจเลขบัญชี" ท้ายสุด (ครบ / มีแค่ 4 ตัวท้าย / ยังไม่มี) — ใช้ตรวจก่อนปิดงวดว่าใครยังโอนเงินเดือนไม่ได้ · ไฟล์เป็น Excel ทุกช่องเก็บเป็นข้อความ เลขบัญชี/เลขบัตรมี 0 ตัวหน้าครบ · แก้ในชีตแล้วบันทึกเป็น CSV เพื่ออัปโหลดกลับได้ทั้งไฟล์ ช่องที่เว้นว่างระบบไม่แตะของเดิม · แผนกชื่อใหม่ถูกเพิ่มเข้าทะเบียนแผนกให้เอง · เลขบัญชีเต็มและเลขบัตรเห็นเฉพาะผู้มีสิทธิ์ออกไฟล์โอน/เจ้าของ',
          }}
          columns={[
            { key: 'emp_code', label: 'รหัสพนักงาน (ห้ามซ้ำ ห้ามเอารหัสเดิมไปใส่คนใหม่) — คอลัมน์เดียวที่บังคับสำหรับแถวคนเดิม', required: true },
            { key: 'first_name', label: 'ชื่อ (* = บังคับเฉพาะคนใหม่)', required: true },
            { key: 'last_name', label: 'นามสกุล', required: true },
            { key: 'email', label: 'อีเมล', required: true },
            { key: 'department', label: 'แผนก', required: true },
            { key: 'position', label: 'ตำแหน่ง', required: true },
            { key: 'start_date', label: 'วันเริ่มงาน YYYY-MM-DD', required: true },
            { key: 'nickname', label: 'ชื่อเล่น' },
            { key: 'national_id', label: 'เลขบัตรประชาชน 13 หลัก (ใช้ยื่น ภงด.1 / 50 ทวิ ปลายปี)' },
            { key: 'birth_date', label: 'วันเกิด YYYY-MM-DD' },
            { key: 'phone', label: 'เบอร์โทร' },
            { key: 'supervisor_email', label: 'อีเมลหัวหน้า (ใช้จัดสายอนุมัติ)' },
            { key: 'employee_type', label: 'ชนิดการจ้าง: monthly (รายเดือน) / daily (รายวัน) / hourly (รายชั่วโมง)' },
            { key: 'status', label: 'สถานะ: active / probation / pending_start / resigned / terminated' },
            { key: 'end_date', label: 'วันพ้นสภาพ YYYY-MM-DD (เฉพาะคนที่ออกแล้ว)' },
            { key: 'sso_number', label: 'เลขประกันสังคม' },
            { key: 'bank', label: 'ธนาคารรับเงินเดือน: KBANK / SCB / BBL / KTB / BAY / TTB / GSB / BAAC (หรือชื่อภาษาไทย)' },
            { key: 'bank_branch', label: 'สาขา' },
            { key: 'bank_account', label: 'เลขบัญชีเต็ม 10-15 หลัก — ตั้งคอลัมน์ในชีตเป็น "ข้อความ" ก่อนพิมพ์ ไม่งั้นเลข 0 ตัวหน้าหาย · เว้นว่าง = ไม่แตะเลขเดิม' },
          ]}
          example={['EMP001', 'สมชาย', 'ใจดี', 'somchai@company.co.th', 'บัญชี', 'นักบัญชี',
                    '2024-03-01', 'ชาย', '1234567890121', '1995-05-20', '0812345678',
                    'manager@company.co.th', 'monthly', 'active', '', '1234567890',
                    'KBANK', 'สยามสแควร์', '0123456789']}
          note="เลขบัตรประชาชน: ถ้าคอลัมน์นี้ไม่มีในไฟล์ หรือปล่อยว่างไว้ ระบบจะไม่แตะของเดิม (ล้างทิ้งไม่ได้ผ่านการนำเข้า เพราะเป็นหลักฐานยืนยันตัวตน) · รหัสเดิมที่เลขบัตรหรือชื่อ-สกุลเป็นคนละคน จะถูกปฏิเสธ เพื่อไม่ให้ประวัติเงินเดือนของคนเก่าย้ายไปติดคนใหม่"
        />

        <StepHeader n={2} title="ฐานเงินเดือนและสิทธิ์การลา" detail="ตั้งค่าปัจจุบัน ใช้คิดเงินเดือนงวดถัดไป" />

        <ImportCard
          title="ฐานเงินเดือน / ค่าจ้าง"
          description="อัตราค่าจ้างปัจจุบันของแต่ละคน — เก็บแบบเข้ารหัส เห็นได้เฉพาะผู้มีสิทธิ์"
          endpoint="/owner/import/salary-master"
          templateName="salary-template.csv"
          columns={[
            { key: 'emp_code', label: 'รหัสพนักงาน (ต้องมีในทะเบียนแล้ว)', required: true },
            { key: 'effective_date', label: 'วันที่อัตรานี้เริ่มมีผล YYYY-MM-DD', required: true },
            { key: 'base_salary', label: 'อัตราค่าจ้าง (รายเดือน/รายวัน/รายชั่วโมง ตามชนิดการจ้างของคนนั้น)', required: true },
            { key: 'sso_eligible', label: 'เข้าประกันสังคมไหม: true / false (เว้นว่าง = true)' },
            { key: 'pf_rate', label: 'อัตรากองทุนสำรองเลี้ยงชีพ % (เว้นว่าง = 0)' },
            { key: 'note', label: 'หมายเหตุ เช่น ปรับขึ้นประจำปี' },
          ]}
          example={['EMP001', '2026-01-01', '30000', 'true', '3', 'ปรับขึ้นประจำปี 2569']}
          note="ตารางนี้เก็บแบบต่อท้าย แก้ทับของเดิมไม่ได้ — ขึ้นเงินเดือนให้เพิ่มแถวใหม่ที่มีวันที่มีผลใหม่ ระบบจะใช้อัตราล่าสุด ณ วันนั้นเอง · แถวที่วันที่มีผลซ้ำกับที่มีอยู่แล้วจะถูกข้ามพร้อมบอกเหตุผล"
        />

        <ImportCard
          title="สิทธิ์การลาต่อปี"
          description="จำนวนวันลาที่แต่ละคนมีสิทธิ์ในปีนั้น รวมวันที่ยกยอดมาจากปีก่อน"
          endpoint="/owner/import/leave-quota"
          templateName="leave-quota-template.csv"
          columns={[
            { key: 'emp_code', label: 'รหัสพนักงาน (ต้องมีในทะเบียนแล้ว)', required: true },
            { key: 'year', label: 'ปี ค.ศ. เช่น 2026 (ห้ามใส่ พ.ศ.)', required: true },
            { key: 'sick_quota', label: 'สิทธิ์ลาป่วย (วัน)' },
            { key: 'personal_quota', label: 'สิทธิ์ลากิจ (วัน)' },
            { key: 'vacation_quota', label: 'สิทธิ์ลาพักร้อน (วัน)' },
            { key: 'carried_days', label: 'วันพักร้อนที่ยกยอดมาจากปีก่อน' },
          ]}
          example={['EMP001', '2026', '30', '3', '6', '2']}
          note="คอลัมน์ที่ไม่มีในไฟล์ หรือปล่อยว่างไว้ = ไม่แตะสิทธิ์เดิมของช่องนั้น (ไม่ใช่ตั้งเป็น 0) · คนที่มีสิทธิ์ของปีนั้นอยู่แล้วจะถูกอัปเดตทับ ไม่ใช่เพิ่มซ้ำ"
        />

        <StepHeader n={3} title="ข้อมูลย้อนหลัง" detail="เฉพาะบริษัทที่ย้ายมาระหว่างปี — ทำให้รายงานปลายปีครบทั้งปี" />

        <p className="card p-3 text-sm text-gray-600 bg-blue-50 border-blue-100">
          เหมาะกับบริษัทที่ย้ายมา PayPilot กลางปี — นำเข้ายอดเดือนที่ผ่านมา เพื่อให้ ภงด.1 / 50 ทวิ / ปกส.
          และการคิดภาษีแบบสะสมของงวดถัดไปถูกต้องครบทั้งปี
        </p>

        <ImportCard
          title="เงินเดือน / ภาษี / ปกส. รายเดือน"
          description="ยอดเดือนย้อนหลังต่อพนักงาน — เขียนเป็นงวดที่ปิดแล้ว และอัปเดตยอดสะสมทั้งปีให้อัตโนมัติ · ระบุพนักงานด้วยรหัสพนักงาน หรือเลขบัตรประชาชนก็ได้"
          endpoint="/owner/import/payroll-history"
          templateName="payroll-history-template.csv"
          columns={[
            { key: 'period', label: 'งวด YYYY-MM เช่น 2026-01', required: true },
            { key: 'emp_code', label: 'รหัสพนักงาน — หรือใช้ national_id แทนได้', required: true },
            { key: 'national_id', label: 'เลขบัตรประชาชน 13 หลัก (ใช้แทน emp_code ได้ เมื่อรหัสพนักงานจากระบบเดิมไม่ตรงกับในระบบนี้)' },
            { key: 'gross', label: 'รายได้รวม (บาท)', required: true },
            { key: 'taxable', label: 'เงินได้ที่ต้องเสียภาษี (เว้นว่าง = ใช้รายได้รวม)' },
            { key: 'tax', label: 'ภาษีหัก ณ ที่จ่าย' },
            { key: 'sso', label: 'ประกันสังคม' },
            { key: 'pf', label: 'กองทุนสำรองเลี้ยงชีพ' },
            { key: 'welfare_fund', label: 'กองทุนสงเคราะห์ลูกจ้าง (กสล.)' },
            { key: 'student_loan', label: 'เงินกู้ยืม กยศ./กรอ.' },
            { key: 'other_deductions', label: 'รายการหักอื่น ๆ / รายการปรับลด (เช่น หักเบิกล่วงหน้า หักของเสีย) — หักจากสุทธิ ไม่ลดเงินได้ที่ต้องเสียภาษี' },
            { key: 'net', label: 'เงินสุทธิ (เว้นว่าง = รายได้รวม − ภาษี − ปกส. − กองทุน − กยศ. − หักอื่น ๆ)' },
          ]}
          example={['2026-01', 'EMP001', '', '30000', '30000', '500', '750', '0', '0', '0', '200', '28550']}
          note="งวดที่ปิดงวดในระบบแล้วจะไม่ถูกทับ (ข้ามให้อัตโนมัติ) · ร่างที่ไม่เคยปิดของเดือนก่อนงวดล่าสุดที่ปิดแล้ว จะถูกแทนด้วยยอดที่นำเข้า"
        />
        {/* fb#254 — ไฟล์ "Payroll Year-to-date Report" จากระบบเดิมของ TN อัปโหลดได้ทั้งไฟล์ */}
        <div className="card p-3 text-sm text-gray-700 bg-teal-50 border-teal-100 space-y-1">
          <p className="font-semibold">มีไฟล์ "รายงานเงินเดือน/ค่าจ้างสะสมรายพนักงาน" จากระบบเดิม?</p>
          <p>อัปโหลดไฟล์ CSV นั้นได้เลยที่ปุ่มอัปโหลดของชุดนี้ ไม่ต้องแปลงเป็นเทมเพลต — ระบบจับคู่พนักงานด้วย <b>เลขบัตรประจำตัวประชาชน</b> และอ่านงวดจากบรรทัด "ช่วงเวลา" (เช่น 1 ม.ค. 2026 - 31 ส.ค. 2026 → ลงยอดสะสมที่งวด 2026-08)</p>
          <p>คอลัมน์ที่ใช้: รวมเงินได้ · รวมประกันสังคม · รวมหัก ณ ที่จ่าย · รวมกองทุนสงเคราะห์ลูกจ้าง · เงินกู้ยืม กยศ./กรอ. · รวมรายการปรับลด (รายการหักอื่น ๆ สะสม) · ยอดจ่ายพนักงาน</p>
          <p className="text-gray-500">คนที่ยังไม่มีเลขบัตรในทะเบียน ระบบจะแจ้งชื่อทีละแถว — เติมเลขบัตรในทะเบียนพนักงานแล้วอัปโหลดไฟล์เดิมซ้ำได้ (คนที่นำเข้าไปแล้วจะถูกเขียนทับด้วยยอดเดิม ไม่ซ้ำ)</p>
        </div>
        {/* fb#158 — ลูกค้าถาม "รายได้สะสม/ภาษี ดึงข้อมูลเก่าอัพโหลดได้ไหม" = มีอยู่แล้ว แต่ไม่รู้ว่าใส่ยอดรวมแถวเดียวได้ */}
        <div className="card p-3 text-sm text-gray-700 bg-amber-50 border-amber-100 space-y-1">
          <p className="font-semibold">มีแค่ยอดสะสมจากสลิปเก่า (ไม่มีแยกรายเดือน)?</p>
          <p>ใส่ 1 แถวต่อพนักงาน · งวด = เดือนสุดท้ายที่จ่ายจากระบบเดิม · รายได้รวม = "เงินได้สะสม" · ภาษี = "ภาษีหัก ณ ที่จ่ายสะสม" · ประกันสังคม = "เงินประกันสังคมสะสม" จากสลิปเดือนนั้น</p>
          <p>เช่น จ่าย ส.ค. จากระบบเดิม → งวด 2026-08 ใส่ยอดสะสม ม.ค.–ส.ค. · สลิปงวดถัดไปจะนับรายได้สะสม/ภาษีสะสมต่อจากยอดนี้ และภาษีสะสมแบบ ภ.ง.ด.1 คิดถูกทั้งปี</p>
          <p className="text-gray-500">รายงานรายเดือน (ภ.ง.ด.1 ของเดือนก่อนหน้า) จะเห็นเป็นก้อนเดียวในงวดนั้น — ใช้ยื่นจากระบบเดิมตามปกติ</p>
        </div>

        <ImportCard
          title="ประวัติการลางาน"
          description="วันลาที่อนุมัติแล้วในอดีต — บันทึกเป็นสถานะอนุมัติ (วันละ 1 แถว) รองรับลารายชั่วโมง"
          endpoint="/owner/import/leave-history"
          templateName="leave-history-template.csv"
          columns={[
            { key: 'emp_code', label: 'รหัสพนักงาน', required: true },
            { key: 'date', label: 'วันที่ลา YYYY-MM-DD', required: true },
            { key: 'leave_type', label: 'ชนิดลา: sick/personal/vacation/unpaid/maternity', required: true },
            { key: 'period', label: 'ช่วง: full/morning/afternoon/custom (เว้นว่าง = full)' },
            { key: 'start_time', label: 'เวลาเริ่ม HH:MM (เฉพาะ custom = ลารายชั่วโมง)' },
            { key: 'end_time', label: 'เวลาสิ้นสุด HH:MM (เฉพาะ custom)' },
            { key: 'hours', label: 'จำนวนชั่วโมง (ใช้แทน start/end ได้เมื่อ custom)' },
            { key: 'minutes', label: 'จำนวนนาที (ใช้แทน start/end หรือ hours ได้เมื่อ custom, 1-480)' },
            { key: 'reason', label: 'เหตุผล (ไม่บังคับ)' },
          ]}
          example={['EMP001', '2026-02-14', 'personal', 'custom', '09:00', '11:00', '', '', 'ลารายชั่วโมง 2 ชม.']}
          note="ลารายชั่วโมง: ใส่ period=custom แล้วระบุอย่างใดอย่างหนึ่ง — start_time+end_time หรือ hours หรือ minutes (8 ชม.=1 วัน, ห้ามกรอกซ้อนกันหลายแบบในแถวเดียว). ลา 20 นาที → ใส่ minutes=20 (อย่าใส่ 0.2 ในช่อง hours เพราะ 0.2 ชม. = 12 นาที ไม่ใช่ 20 นาที). ถ้ามีวันลาของคนนั้นในวันเดียวกันอยู่แล้ว จะข้ามให้ (กันซ้ำ)"
        />
      </div>
    </AdminLayout>
  );
}
